import { Group, Section } from "anketa-core/group";
import { Translation } from "anketa-core/i18n";
import { OpenQuestion } from "anketa-core/open_question";
import { OptionsQuestion } from "anketa-core/options_question";
import { TextValidator } from "anketa-core/validation";

export const setupSentinalOneGroup = (
  sentinalOneScanners: Section,
  idx = 0
) => {
  const sentinalOneGroup = sentinalOneScanner(idx);
  sentinalOneScanners.addChild(sentinalOneGroup.sentinalOneScanner);

  function onConfigChange() {
    const name = sentinalOneGroup.sentinalOneName.fact.raw;

    if (name && name.toString().trim() !== "") {
      sentinalOneGroup.sentinalOneName.removeChangeListener(onConfigChange);
      setupSentinalOneGroup(sentinalOneScanners, idx + 1);
    }
  }

  sentinalOneGroup.sentinalOneName.addChangeListener(onConfigChange);
};

export const sentinalOneScanner = (
  idx: number
): { sentinalOneScanner: Group; sentinalOneName: OpenQuestion } => {
  const idPrefix = `sone_scanner_${idx}`;

  const sentinalOneScanner = new Group(idPrefix, [
    new Translation("en", "Unconfigured SentinalOne Scanner"),
    new Translation("de", "Unkonfigurierter SentinalOne Scanner"),
  ]);

  const name = new OpenQuestion(`${idPrefix}_name`, [
    new Translation("en", "Display name"),
    new Translation("de", "Anzeigename"),
  ]);
  name.validator = new TextValidator();

  name.required = true;
  name.addChangeListener(() => {
    const plainName = name.fact.raw.toString();
    if (plainName.trim() !== "") {
      sentinalOneScanner.title = [
        new Translation("en", plainName),
        new Translation("de", plainName),
      ];
    } else {
      sentinalOneScanner.title = [
        new Translation("en", "Unconfigured SentinalOne Scanner"),
        new Translation("de", "Unkonfigurierter SentinalOne Scanner"),
      ];
    }
  });
  sentinalOneScanner.addChild(name);

  const s1CustomerDomain = new OpenQuestion(`${idPrefix}_customer_domain`, [
    new Translation("en", "SentinelOne Customer Domain"),
    new Translation("de", "SentinelOne-Kundendomäne"),
  ]);
  s1CustomerDomain.validator = new TextValidator();

  s1CustomerDomain.required = true;
  s1CustomerDomain.help = [
    new Translation(
      "en",
      "The domain part of your SentinelOne console URL (e.g., mycompany.sentinelone.net)."
    ),
    new Translation(
      "de",
      "Der Domain-Teil Ihrer SentinelOne-Konsole (z.B. mycompany.sentinelone.net)."
    ),
  ];
  sentinalOneScanner.addChild(s1CustomerDomain);

  const s1Insecure = new OptionsQuestion(
    `${idPrefix}_insecure`,
    [
      new Translation("en", "Disable Certificate Verification"),
      new Translation("de", "Zertifikatsüberprüfung deaktivieren"),
    ],
    "default.options.yes_no"
  );
  s1Insecure.help = [
    new Translation(
      "en",
      "Should SSL/TLS certificate verification be disabled? Only use this for testing with self-signed certificates."
    ),
    new Translation(
      "de",
      "Soll die Überprüfung von SSL/TLS-Zertifikaten deaktiviert werden? Nur für Tests mit selbstsignierten Zertifikaten verwenden."
    ),
  ];
  if (s1Insecure.fact.raw === null) {
    s1Insecure.setFactValue(1);
  }
  sentinalOneScanner.addChild(s1Insecure);

  const s1Proxy = new OpenQuestion(`${idPrefix}_proxy`, [
    new Translation("en", "Proxy Configuration"),
    new Translation("de", "Proxy-Konfiguration"),
  ]);
  s1Proxy.validator = new TextValidator();
  s1Proxy.help = [
    new Translation(
      "en",
      "Optional: JSON-formatted proxy settings if your environment requires a proxy for outbound connections."
    ),
    new Translation(
      "de",
      "Optional: JSON-formatierte Proxy-Einstellungen, wenn Ihre Umgebung einen Proxy für ausgehende Verbindungen benötigt."
    ),
  ];
  sentinalOneScanner.addChild(s1Proxy);

  const s1ApiKey = new OpenQuestion(`${idPrefix}_api_key`, [
    new Translation("en", "API Key"),
    new Translation("de", "API-Schlüssel"),
  ]);
  s1ApiKey.required = true;
  s1ApiKey.readonly = true;
  s1ApiKey.help = [
    new Translation(
      "en",
      "A SentinelOne API key with read-only access to devices and applications."
    ),
    new Translation(
      "de",
      "Ein SentinelOne API-Schlüssel mit Nur-Lesen-Zugriff auf Geräte und Applikationen."
    ),
  ];
  sentinalOneScanner.addChild(s1ApiKey);

  return { sentinalOneScanner, sentinalOneName: name };
};
